import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteForecastsuppliersFromAPI,
  fetchForecastsuppliersFromAPI,
  uploadForecastsupplierFileToAPI,
  downloadForecastsupplierFileFromAPI,
  registerFeedbackForecastsupplier
} from "../../../services/ForecastsupplierService";

const safeGetLocalStorage = (key) => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const getStoredUser = () => {
  const rawUser = safeGetLocalStorage("user");
  if (!rawUser) return {};
  try {
    return JSON.parse(rawUser);
  } catch (error) {
    return {};
  }
};

const resolveDefaultVendorCode = (user) => {
  const active = safeGetLocalStorage("active_vendor_code");
  if (active) return active;
  if (user?.active_vendor_code) return user.active_vendor_code;
  if (user?.primary_vendor_code) return user.primary_vendor_code;
  if (user?.vendor_code) return user.vendor_code;
  if (Array.isArray(user?.vendor_codes) && user.vendor_codes.length > 0) {
    return user.vendor_codes[0];
  }
  return null;
};

const storedUser = getStoredUser();
const defaultVendorCode = resolveDefaultVendorCode(storedUser);
const initialKeywordSearch = {};
if ((storedUser?.role || '').toLowerCase() === 'vendor' && defaultVendorCode) {
  initialKeywordSearch.vendorCode = defaultVendorCode;
}

// Fetch Forecastsuppliers
export const fetchForecastsuppliers = createAsyncThunk(
  "forecastsuppliers/fetchForecastsuppliers",
  async ({ page, perPage, search }, { rejectWithValue }) => {
    const user = getStoredUser();
    const normalizedSearch = search && typeof search === 'object' ? search : {};
    const role = (user?.role || '').toLowerCase();

    let vendorCode = normalizedSearch.vendorCode || normalizedSearch.vendor_code || null;
    if (role === 'vendor') {
      vendorCode =
        vendorCode ||
        safeGetLocalStorage("active_vendor_code") ||
        user?.active_vendor_code ||
        user?.primary_vendor_code ||
        user?.vendor_code ||
        (Array.isArray(user?.vendor_codes) && user.vendor_codes.length > 0 ? user.vendor_codes[0] : null);
    }

    const searchData = {
      vendor_code: vendorCode,
      period: normalizedSearch.period,
      feedback: normalizedSearch.feedback,
      commodity: normalizedSearch.commodity,
      plant: normalizedSearch.plant,
    };

    Object.keys(searchData).forEach((key) => {
      if (!searchData[key]) {
        delete searchData[key];
      }
    });

    // Anything the table sends — the per-column funnel filters (f_*) and the
    // sort — is forwarded as-is. The explicit list above exists to work out the
    // vendor scope for a supplier account, NOT to be an allow-list for the whole
    // request: rebuilding the object from those five keys silently dropped
    // sort_by/sort_dir and every f_* filter, so sorting and column filtering
    // never reached the API at all.
    Object.entries(normalizedSearch).forEach(([key, value]) => {
      const isPassThrough =
        key.startsWith('f_') || key === 'sort_by' || key === 'sort_dir';
      if (
        isPassThrough &&
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ''
      ) {
        searchData[key] = value;
      }
    });

    try {
      const response = await fetchForecastsuppliersFromAPI(page, perPage, searchData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch forecastsupplier data.");
    }
  }
);

// Delete Forecastsupplier
export const deleteForecastsupplier = createAsyncThunk(
  "forecastsuppliers/deleteForecastsupplier",
  async (forecastsupplierId, { rejectWithValue }) => {
    try {
      const response = await deleteForecastsuppliersFromAPI(forecastsupplierId);
      return forecastsupplierId;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete forecastsupplier.");
    }
  }
);

// Delete Forecastsupplier
export const feedbackForecastsupplier = createAsyncThunk(
  "forecastsuppliers/feedbackForecastsupplier",
  async (data
    , { rejectWithValue }) => {
    try {
      const response = await registerFeedbackForecastsupplier(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete forecastsupplier.");
    }
  }
);

// Upload Forecastsupplier File
export const uploadForecastsupplierFile = createAsyncThunk(
  "forecastsuppliers/uploadForecastsupplierFile",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await uploadForecastsupplierFileToAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to upload forecastsupplier file.");
    }
  }
);

// Download Forecastsupplier File
export const downloadForecastsupplierFile = createAsyncThunk(
  "forecastsuppliers/downloadForecastsupplierFile",
  async ({ fileType, vendorCode, period, commodity, plant }, { rejectWithValue }) => {
    try {
      const response = await downloadForecastsupplierFileFromAPI(
        fileType,
        vendorCode,
        period,
        commodity,
        plant
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to download forecastsupplier file.");
    }
  }
);

const forecastsupplierSlice = createSlice({
  name: "forecastsuppliers",
  initialState: {
    forecastsuppliers: [],
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    firstItem: 1,
    forecastsupplierSearch: "",
    loading: false,
    error: null,
    totalCount: 0,
    keywordSearch: initialKeywordSearch,
  },
  reducers: {
    setSearchForecastsupplier: (state, action) => {
      state.forecastsupplierSearch = action.payload;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setVisibilityFilter: (state, action) => {
      state.currentFilter = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setKeywordSearch: (state, action) => {
      state.keywordSearch = action.payload || {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Forecastsuppliers
      .addCase(fetchForecastsuppliers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchForecastsuppliers.fulfilled, (state, action) => {
        const forecastsuppliers = action.payload.forecast;
        state.loading = false;
        state.forecastsuppliers = forecastsuppliers.data;
        state.currentPage = forecastsuppliers.current_page;
        state.lastPage = forecastsuppliers.last_page;
        state.perPage = forecastsuppliers.per_page;
        state.firstItem = forecastsuppliers.from;
        state.totalCount = forecastsuppliers.total;
      })
      .addCase(fetchForecastsuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Forecastsupplier
      .addCase(deleteForecastsupplier.fulfilled, (state, action) => {
        state.forecastsuppliers = state.forecastsuppliers.filter((forecastsupplier) => forecastsupplier.id !== action.payload);
      })
      .addCase(deleteForecastsupplier.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Upload Forecastsupplier File
      .addCase(uploadForecastsupplierFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadForecastsupplierFile.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Download Forecastsupplier File
      .addCase(downloadForecastsupplierFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadForecastsupplierFile.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  setSearchForecastsupplier,
  setPage,
  setVisibilityFilter,
  setPerPage,
  setKeywordSearch,
} = forecastsupplierSlice.actions;
export default forecastsupplierSlice.reducer;
